import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@platform/ui';
import { useAppDispatch } from '@platform/state/hooks';
import { rightPanelChanged } from '@platform/state/slices/uiSlice';
import { AiMessageBubble } from './ai/AiMessageBubble';
import { useAiAsk } from './ai/useAiAsk';
import { useAiContext } from './ai/useAiContext';
import { clearAiMessages, useAiConversation } from './aiConversation';
import type { IAiPanelProps } from './IAiPanelProps';
import './aiPanel.css';

/**
 * Права панель ШІ-асистента (UIUX patterns/ai-assistant, layout/right-panels, прототип v4): min(380px, 42%), штовхає вміст; шапка — «ШІ»
 * у градієнтному колі, «ШІ-асистент», «Контекст: простір › модуль (сторінка) › запис», «Новий», ×; порожня розмова — пояснення й три
 * підказки для поточної сторінки; питання — акцентні праворуч, відповіді (Markdown) — на --surface2 ліворуч; «Думаю…», поки асистент
 * відповідає; поле «Запитайте асистента…». «Запитати ШІ» на записі надсилає питання одразу (подія po:ai-ask, useAiBridge).
 */
export function AiPanel({ ws }: IAiPanelProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { messages, busy } = useAiConversation();
  const [input, setInput] = useState('');
  const context = useAiContext(ws);
  const ask = useAiAsk(ws, context);
  const body = useRef<HTMLDivElement>(null);
  const suggestions = t(`shell.aiSuggest.${context.kind}`, { returnObjects: true, module: context.moduleName }) as unknown as string[];
  useEffect(() => { body.current?.scrollTo({ top: body.current.scrollHeight }); }, [messages.length, busy]);
  const submit = (e: FormEvent) => { e.preventDefault(); if (busy || !input.trim()) return; ask(input); setInput(''); };
  return (
    <aside className="po-panel ai-panel" aria-label={t('shell.aiAssistant')}>
      <div className="po-panel__head">
        <span className="ai-panel__badge" aria-hidden="true">{t('shell.ai')}</span>
        <div className="ai-panel__titles">
          <span className="po-panel__title">{t('shell.aiAssistant')}</span>
          <span className="po-panel__subtitle" title={context.path}>{t('shell.aiContext', { context: context.path })}</span>
        </div>
        <button type="button" className="ai-panel__new" title={t('shell.aiNewTitle')} aria-label={t('shell.aiNewTitle')} onClick={() => { clearAiMessages(); setInput(''); }}>{t('shell.aiNew')}</button>
        <button type="button" className="ai-panel__close" aria-label={t('shell.aiClose')} title={t('shell.aiClose')} onClick={() => dispatch(rightPanelChanged(null))}>
          <Icon name="x" size={14} strokeWidth={2} />
        </button>
      </div>
      <div ref={body} className="po-panel__body" aria-live="polite">
        {!messages.length && !busy && (
          <div className="ai-panel__intro">
            <div className="muted">{t('shell.aiIntro')}</div>
            {Array.isArray(suggestions) && suggestions.map((s) => <button key={s} type="button" className="ai-panel__suggest" onClick={() => ask(s)}>{s}</button>)}
          </div>
        )}
        {messages.map((m, i) => <AiMessageBubble key={i} message={m} ws={ws} />)}
        {busy && <div className="ai-panel__thinking">{t('aiPanel.thinking')}</div>}
      </div>
      <form className="ai-panel__composer" onSubmit={submit}>
        <input className="ai-panel__input" value={input} placeholder={t('shell.aiPlaceholder')} aria-label={t('shell.aiMessage')} onChange={(e) => setInput(e.target.value)} />
        <button type="submit" className="ai-panel__send" disabled={busy}>{t('shell.aiSend')}</button>
      </form>
    </aside>
  );
}
